import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { Tooltip } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LanguageIcon from "@mui/icons-material/Language";
import Restricted from "@/consumer/restricted";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

export default function ProbeTable() {
  const { errorModal } = useErrorModal();

  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageHeading(t("probe"));

  const customRowActions = (row: any) => {
    return (
      <>
        <Restricted permissionRequired="frontend_view_entity_translation">
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("translation_edit")}
          >
            <GenericButton
              variant="icon"
              onClick={() => navigateToTranslationEdit(row)}
            >
              <LanguageIcon />
            </GenericButton>
          </Tooltip>
        </Restricted>
      </>
    );
  };

  const navigateToTranslationEdit = (row: any) => {
    navigate(`/probe/${row.original.id}/translation`);
  };

  return (
    <>
      <GenericCrudTable
        entity={"probe"}
        additionalUrlParameter={{ include_relations: true }}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        customRowActions={customRowActions}
        allowOpenEditInNewTab
        customEditHandler={(row: any) => {
          const editUrl = `/settings/probeEditor/${row.original.id}`;

          if (row.allowOpenInNewTab) {
            window.open(editUrl, "_blank", "noopener,noreferrer");
          } else {
            navigate(editUrl);
          }
        }}
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: t("description"),
            accessorKey: "description",
            id: "description",
            enableForCreate: true,
            textarea: true,
          },
          {
            header: t("examination_set"),
            accessorKey: "examination_set",
            id: "examination_set",
            customColumnFilterId: "examination_set__name",
            required: true,
            errorMessage: t("field_required"),
            accessorFn: (row: any) => {
              if (row.examination_set.length !== 0) {
                return (
                  <>
                    {row.examination_set
                      .sort((a: any, b: any) => a.name.localeCompare(b.name))
                      .map((item: any) => {
                        return <div key={item.id}>{item.name}</div>;
                      })}
                  </>
                );
              } else {
                return <div>-</div>;
              }
            },

            enableForCreate: true,
            multiselect: true,
            entity: "examination",
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            id: "created_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",

            id: "created_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            id: "modified_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",

            id: "modified_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
        ]}
      />
      {errorModal}
    </>
  );
}
